import { ChartDataset, ChartType, Plugin } from "chart.js";
import { subDays } from "date-fns";

import { ANALYSIS_DATA_TYPE } from "@/constants";
import { SearchFilter } from "@/types/SearchFilter";
import { deleteNil, ensureIsDate, formatDateForApi } from "@/utils";

const ClickHandlerPlugin: Omit<
  Plugin<ChartType>,
  // These two props are the only thing that differs between line/pie charts.
  // So by removing them we can (typesafely) use this plugin for both.
  "beforeTooltipDraw" | "afterTooltipDraw"
> = {
  id: "click",
  install(chart, _args, { onClick }) {
    if (onClick) {
      // setup click handler

      chart.options.onClick = (event, elements) => {
        const { labels } = chart.config.data;
        const datasets = chart.config.data
          .datasets as ChartDataset<ChartType>[];

        if (elements.length === 0) return;

        const firstElement = elements[0];
        const {
          datasetIndex, // clicked data point index
          index, // clicked dataset index
        } = firstElement;

        const dataset = datasets[datasetIndex];
        if (dataset.data_type === ANALYSIS_DATA_TYPE.INDUSTRY_INDEX) return;
        const label = ensureIsDate(labels?.[index], undefined);
        const nextLabel = ensureIsDate(labels?.[index + 1], undefined);
        // Get any insight that might exist for this data point
        const insight_ids = dataset.insights?.[index];
        // merge the applied filters with the selected date range
        const filters = deleteNil({
          // get filters from the dataset here
          // The array check here is for doughnut/pies which only have one dataset.
          // In that case the applied_filters needs to be in an array with the indices matching each dataset.data entries
          ...((Array.isArray(dataset.applied_filters)
            ? dataset.applied_filters[index]
            : dataset.applied_filters) || {}),
          start_date: label ? formatDateForApi(label) : undefined,
          insight_ids: insight_ids ? insight_ids : undefined,
          end_date: nextLabel
            ? formatDateForApi(subDays(nextLabel, 1))
            : undefined,
        });

        // Feed back the filters to be used
        onClick(filters, label, datasetIndex, index);
      };
    }
  },
  defaults: {
    onClick: undefined,
  },
};

export type ClickHandlerPluginOptions = {
  onClick?: (filters: SearchFilter, label: string) => void;
};

export default ClickHandlerPlugin;
